import React, { useEffect, useState } from 'react';
import Select, { StylesConfig } from 'react-select';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { updateOneReservations } from '../../../../shared/services/data.service';
import { birdToast } from '../../../../../shared/components/core-ui/toast';

interface AmountCurrency {
  amount: string;
  currency: string;
}

const CURRENCY_OPTIONS = [
  { value: 'egp', label: 'EGP' },
  { value: 'usd', label: 'USD' }
];

type CurrencyOption = (typeof CURRENCY_OPTIONS)[number];

const currencySelectStyles: StylesConfig<CurrencyOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '28px',
    width: '84px',
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 4px' }),
  singleValue: (base) => ({
    ...base,
    fontSize: '14px',
    color: '#292a2b',
    fontFamily: 'Gilroy-Medium'
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: '2px', color: '#707071' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    marginTop: '6px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #e6e8ec',
    boxShadow: '0px 2px 6px 2px rgba(0,0,0,0.08)'
  }),
  menuList: (base) => ({ ...base, padding: '6px' }),
  option: (base, state) => ({
    ...base,
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: state.isSelected ? 'Gilroy-Medium' : 'Gilroy-Regular',
    color: '#292a2b',
    backgroundColor: state.isSelected ? '#f8f4f1' : state.isFocused ? '#faf8f6' : 'transparent',
    cursor: 'pointer'
  })
};

const FINANCE_ROLES = [
  UserRole.FINANCE_MANAGER,
  UserRole.FINANCE_SPECIALIST,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN
];

export interface FinancialAdjustmentsProps {
  data: {
    reservation: ReservationDetailsUiModel;
  };
  config: {
    isPermited: (roles: UserRole[]) => boolean;
  };
}

export const FinancialAdjustments: React.FC<FinancialAdjustmentsProps> = ({
  data: { reservation },
  config: { isPermited }
}) => {
  const canEdit = isPermited(FINANCE_ROLES);

  const [refundInput, setRefundInput] = useState<AmountCurrency>({ amount: '', currency: 'egp' });
  const [compensationInput, setCompensationInput] = useState<AmountCurrency>({
    amount: '',
    currency: 'egp'
  });
  const [insuranceInput, setInsuranceInput] = useState<AmountCurrency>({
    amount: '',
    currency: 'egp'
  });
  const [isSavingFinancial, setIsSavingFinancial] = useState(false);

  useEffect(() => {
    if (!reservation) return;
    setRefundInput({
      amount:
        reservation.refund?.amount !== undefined && reservation.refund?.amount !== null
          ? String(reservation.refund.amount)
          : '',
      currency: reservation.refund?.currency || 'egp'
    });
    setCompensationInput({
      amount:
        reservation.compensation?.amount !== undefined && reservation.compensation?.amount !== null
          ? String(reservation.compensation.amount)
          : '',
      currency: reservation.compensation?.currency || 'egp'
    });
    setInsuranceInput({
      amount:
        reservation.insuranceCollected?.amount !== undefined &&
        reservation.insuranceCollected?.amount !== null
          ? String(reservation.insuranceCollected.amount)
          : '',
      currency: reservation.insuranceCollected?.currency || 'egp'
    });
  }, [reservation]);

  const handleSave = () => {
    if (!reservation) return;
    setIsSavingFinancial(true);
    const refundAmount = refundInput?.amount ? parseFloat(refundInput.amount) : null;
    const compensationAmount = compensationInput?.amount
      ? parseFloat(compensationInput.amount)
      : null;
    const insuranceAmount = insuranceInput?.amount ? parseFloat(insuranceInput.amount) : null;

    const updateData: any = { id: reservation.id };

    if (refundAmount !== null && !isNaN(refundAmount)) {
      updateData.refund = { amount: refundAmount, currency: refundInput.currency };
    } else {
      updateData.refund = null;
    }

    if (compensationAmount !== null && !isNaN(compensationAmount)) {
      updateData.compensation = {
        amount: compensationAmount,
        currency: compensationInput.currency
      };
    } else {
      updateData.compensation = null;
    }

    if (insuranceAmount !== null && !isNaN(insuranceAmount)) {
      updateData.insuranceCollected = {
        amount: insuranceAmount,
        currency: insuranceInput.currency
      };
    } else {
      updateData.insuranceCollected = null;
    }

    const promise = updateOneReservations(updateData).then(() => setIsSavingFinancial(false));

    birdToast.promise(promise, {
      loadingMessage: 'Saving changes...',
      successMessage: 'Financial adjustments saved successfully.',
      errorFailedMessage: 'Failed to save financial adjustments.'
    });
  };

  const fields: {
    label: string;
    value: AmountCurrency;
    onChange: React.Dispatch<React.SetStateAction<AmountCurrency>>;
  }[] = [
    { label: 'Refund', value: refundInput, onChange: setRefundInput },
    { label: 'Compensation', value: compensationInput, onChange: setCompensationInput },
    { label: 'Insurance collected', value: insuranceInput, onChange: setInsuranceInput }
  ];

  return (
    <>
      <h2 className="m-0 text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500">
        Adjustments
      </h2>
      <div className="mt-[20px] grid grid-cols-1 md:grid-cols-3 gap-[12px]">
        {fields.map((field) => (
          <div
            key={field.label}
            className={`rounded-[12px] border px-[12px] pt-[8px] pb-[6px] ${
              canEdit
                ? 'border-neutral-100 focus-within:border-neutral-500 focus-within:ring-1 focus-within:ring-neutral-500'
                : 'border-gray-200 bg-creams-100'
            }`}
          >
            <label
              htmlFor={`adjustment-${field.label}`}
              className="block mb-0 font-gilroy-regular text-[12px] text-neutral-300"
            >
              {field.label}
            </label>
            <div className="flex items-center gap-[8px]">
              <input
                id={`adjustment-${field.label}`}
                type="number"
                inputMode="decimal"
                value={field.value.amount}
                disabled={!canEdit}
                placeholder="0.00"
                onChange={(event) => {
                  const amount = event.target.value;
                  field.onChange((prev) => ({ ...prev, amount }));
                }}
                className="flex-1 min-w-0 bg-transparent outline-none text-[15px] text-neutral-500 py-[2px] disabled:cursor-not-allowed [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <Select<CurrencyOption, false>
                value={CURRENCY_OPTIONS.find((option) => option.value === field.value.currency)}
                options={CURRENCY_OPTIONS}
                isDisabled={!canEdit}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                styles={currencySelectStyles}
                onChange={(selected) => {
                  const currency = selected?.value || 'egp';
                  field.onChange((prev) => ({ ...prev, currency }));
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="mt-[20px] flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSavingFinancial}
            className="px-[24px] py-[12px] rounded-[8px] bg-neutral-500 text-neutral-0 text-[14px] font-gilroy-medium hover:bg-neutral-700 disabled:opacity-60 cursor-pointer"
          >
            {isSavingFinancial ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </>
  );
};
